import { appSettings } from '@/appSettings';

let _planhatInitialized = false;

export type PlanhatIdentifyInfo = {
    email: string;
    name?: string;
    companyExternalId?: string;
};

type UsePlanhatReturn = {
    init: () => void;
    identify: (externalId: string, info: PlanhatIdentifyInfo) => void;
    track: (action: string, extra?: Record<string, unknown>) => void;
};

const PLANTRACK_METHODS = ['init', 'identify', 'track'] as const;

export function usePlanhat(): UsePlanhatReturn {
    function init(): void {
        if (_planhatInitialized) return;
        if (!appSettings.enablePlanhat) return;
        if (appSettings.planhatTenantId === '') return;

        // Planhat tracking-script stub: queues calls until plantrack.min.js loads,
        // then the real script replays the queue. Mirrors the official snippet.
        const w = window;
        w.plantrack = w.plantrack ?? [];

        for (const method of PLANTRACK_METHODS) {
            w.plantrack[method] = function (...args: unknown[]): void {
                w.plantrack.push([method, ...args]);
            };
        }

        w.plantrack.load = function (endpoint: string): void {
            w.plantrack._endpoint = endpoint;
            const scriptElement = document.createElement('script');
            scriptElement.type = 'text/javascript';
            scriptElement.async = true;
            scriptElement.src = 'https://app.planhat.com/analytics/plantrack.min.js';
            const firstScript = document.getElementsByTagName('script')[0];
            firstScript.parentNode?.insertBefore(scriptElement, firstScript);
        };

        w.plantrack.load(`https://analytics.planhat.com/analytics/${appSettings.planhatTenantId}`);
        _planhatInitialized = true;
    }

    function identify(externalId: string, info: PlanhatIdentifyInfo): void {
        if (!_planhatInitialized) return;
        if (window.plantrack == null) return;
        window.plantrack.identify(externalId, info);
    }

    function track(action: string, extra?: Record<string, unknown>): void {
        if (!_planhatInitialized) return;
        if (window.plantrack == null) return;
        window.plantrack.track(action, extra);
    }

    return { init, identify, track };
}
